import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const LiveChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isOnline, setIsOnline] = useState(true);
  const messagesEndRef = useRef(null);

  const initialMessages = [
    {
      id: 1,
      sender: 'agent',
      name: 'Marina Santos',
      message: 'Olá! Bem-vindo à Estação Marina. Como posso ajudá-lo hoje?',
      timestamp: new Date(Date.now() - 60000),
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=40&h=40&fit=crop&crop=face'
    }
  ];

  const quickReplies = [
    'Informações sobre lojas',
    'Horário de funcionamento',
    'Como chegar',
    'Estacionamento',
    'Eventos atuais'
  ];

  const agentInfo = {
    name: 'Marina Santos',
    role: 'Atendimento ao Cliente',
    avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=40&h=40&fit=crop&crop=face',
    status: 'online'
  };

  useEffect(() => {
    if (isOpen && messages?.length === 0) {
      setMessages(initialMessages);
    }
  }, [isOpen]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = (messageText = newMessage) => {
    if (!messageText?.trim()) return;

    const userMessage = {
      id: Date.now(),
      sender: 'user',
      message: messageText,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setNewMessage('');
    setIsTyping(true);

    // Simulate agent response
    setTimeout(() => {
      setIsTyping(false);
      const agentResponse = {
        id: Date.now() + 1,
        sender: 'agent',
        name: agentInfo?.name,
        message: getAgentResponse(messageText),
        timestamp: new Date(),
        avatar: agentInfo?.avatar
      };
      setMessages(prev => [...prev, agentResponse]);
    }, 1500);
  };

  const getAgentResponse = (userMessage) => {
    const message = userMessage?.toLowerCase();
    
    if (message?.includes('loja') || message?.includes('store')) {
      return 'Temos mais de 45 lojas no Estação Marina! Você pode ver todas elas em nosso diretório. Está procurando algo específico?';
    } else if (message?.includes('horário') || message?.includes('funcionamento')) {
      return 'Nosso horário de funcionamento é de segunda a sábado das 9h às 22h, e domingo das 14h às 20h. Algumas lojas podem ter horários diferenciados.';
    } else if (message?.includes('chegar') || message?.includes('localização')) {
      return 'Estamos localizados na Rua Marina da Rocha, 456 - Vila Madalena. A estação de metrô mais próxima é Vila Madalena (Linha Verde), a apenas 5 minutos a pé.';
    } else if (message?.includes('estacionamento') || message?.includes('vaga')) {
      return 'Oferecemos estacionamento gratuito por até 3 horas, com 120 vagas disponíveis. Também temos vagas VIP e para carros elétricos. Quer mais detalhes?';
    } else if (message?.includes('evento') || message?.includes('promoção')) {
      return 'Sempre temos eventos e promoções especiais! Você pode conferir nossa agenda completa na seção de eventos. Posso te ajudar com algo específico?';
    } else {
      return 'Entendi! Posso te ajudar com informações sobre nossas lojas, horários, localização, estacionamento ou eventos. O que você gostaria de saber?';
    }
  };

  const handleKeyPress = (e) => {
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (date) => {
    return date?.toLocaleTimeString('pt-BR', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  return (
    <>
      {/* Chat Toggle Button */}
      <div className="fixed bottom-6 right-6 z-50">
        <Button
          variant="default"
          size="icon"
          className="w-14 h-14 rounded-full shadow-warm-lg hover:shadow-xl transition-all duration-300"
          onClick={() => setIsOpen(!isOpen)}
        >
          <Icon name={isOpen ? "X" : "MessageCircle"} size={24} />
        </Button>
        
        {!isOpen && isOnline && (
          <div className="absolute -top-2 -right-2 w-4 h-4 bg-success rounded-full border-2 border-background animate-pulse" />
        )}
      </div>
      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-24 right-6 w-80 h-96 bg-card border border-border rounded-lg shadow-warm-lg z-50 flex flex-col">
          {/* Chat Header */}
          <div className="flex items-center justify-between p-4 border-b border-border bg-primary rounded-t-lg">
            <div className="flex items-center space-x-3">
              <div className="relative">
                <img
                  src={agentInfo?.avatar}
                  alt={agentInfo?.name}
                  className="w-8 h-8 rounded-full"
                  onError={(e) => {
                    e.target.src = '/assets/images/no_image.png';
                  }}
                />
                <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-success rounded-full border border-primary" />
              </div>
              <div>
                <h4 className="font-medium text-primary-foreground text-sm">
                  {agentInfo?.name}
                </h4>
                <p className="text-xs text-primary-foreground/80">
                  {agentInfo?.role}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="text-primary-foreground hover:bg-primary-foreground/10"
              onClick={() => setIsOpen(false)}
            >
              <Icon name="Minimize2" size={16} />
            </Button>
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages?.map((message) => (
              <div
                key={message?.id}
                className={`flex ${message?.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-xs ${message?.sender === 'user' ? 'order-2' : 'order-1'}`}>
                  {message?.sender === 'agent' && (
                    <div className="flex items-center space-x-2 mb-1">
                      <img
                        src={message?.avatar}
                        alt={message?.name}
                        className="w-5 h-5 rounded-full"
                        onError={(e) => {
                          e.target.src = '/assets/images/no_image.png';
                        }}
                      />
                      <span className="text-xs text-muted-foreground">
                        {message?.name}
                      </span>
                    </div>
                  )}
                  <div
                    className={`p-3 rounded-lg text-sm ${
                      message?.sender === 'user' ?'bg-primary text-primary-foreground' :'bg-muted text-foreground'
                    }`}
                  >
                    {message?.message}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {formatTime(message?.timestamp)}
                  </p>
                </div>
              </div>
            ))}

            {isTyping && (
              <div className="flex justify-start">
                <div className="flex items-center space-x-2 p-3 bg-muted rounded-lg">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" />
                    <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                    <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                  </div>
                  <span className="text-xs text-muted-foreground">digitando...</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Replies */}
          {messages?.length <= 1 && (
            <div className="px-4 py-2 border-t border-border">
              <p className="text-xs text-muted-foreground mb-2">Perguntas frequentes:</p>
              <div className="flex flex-wrap gap-1">
                {quickReplies?.slice(0, 3)?.map((reply, index) => (
                  <button
                    key={index}
                    onClick={() => handleSendMessage(reply)}
                    className="px-2 py-1 text-xs bg-accent/20 text-accent-foreground rounded-full hover:bg-accent/30 transition-colors duration-200"
                  >
                    {reply}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Message Input */}
          <div className="p-4 border-t border-border">
            <div className="flex items-center space-x-2">
              <div className="flex-1 relative">
                <textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e?.target?.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Digite sua mensagem..."
                  rows={1}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-none text-sm"
                />
              </div>
              <Button
                variant="default"
                size="icon"
                onClick={() => handleSendMessage()}
                disabled={!newMessage?.trim()}
                className="w-8 h-8"
              >
                <Icon name="Send" size={14} />
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LiveChat;